import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { AssistantService } from '../../core/assistant/assistant.service';
import { AssetUrlPipe } from '../../core/api/asset-url.pipe';
import { StoreLogo } from '../store-logo/store-logo';
import {
  FrontendAssistantCouponCard,
  FrontendAssistantProductCard,
  FrontendAssistantStoreCard,
} from '../../core/api/api.models';

/**
 * The SavingArena assistant drawer.
 *
 * Right-hand panel on a desktop, full screen on a phone. It renders the conversation held in
 * `AssistantService`, so the homepage field and this panel are two views of the same thread.
 *
 * Every card below is a real catalogue row and links to that row's own page — the panel never
 * shows a price, code or discount that is not stored against the record it came from.
 */
@Component({
  selector: 'app-ai-chat-panel',
  imports: [RouterLink, AssetUrlPipe, StoreLogo],
  templateUrl: './ai-chat-panel.html',
  styleUrl: './ai-chat-panel.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiChatPanel {
  private readonly assistant = inject(AssistantService);

  protected readonly messages = this.assistant.messages;
  protected readonly isOpen = this.assistant.isOpen;
  protected readonly isBusy = this.assistant.isBusy;

  protected readonly draft = signal('');

  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  private readonly input = viewChild<ElementRef<HTMLInputElement>>('input');

  constructor() {
    // Keep the newest turn in view, and put the caret in the field as the panel opens.
    effect(() => {
      const open = this.isOpen();
      this.messages();
      if (!open) return;
      queueMicrotask(() => {
        const box = this.scroller()?.nativeElement;
        if (box) box.scrollTop = box.scrollHeight;
        this.input()?.nativeElement.focus();
      });
    });
  }

  protected close(): void {
    this.assistant.close();
  }

  protected send(): void {
    const text = this.draft().trim();
    if (!text) return;
    this.assistant.ask(text);
    this.draft.set('');
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.send();
    }
    if (event.key === 'Escape') this.close();
  }

  /** A suggestion chip asks its own question, so the visitor never has to retype it. */
  protected useSuggestion(text: string): void {
    this.assistant.ask(text);
  }

  protected onDraft(value: string): void {
    this.draft.set(value);
  }

  // --- link targets, matching the routes the rest of the storefront uses --------------------------

  protected storeLink(store: FrontendAssistantStoreCard): string {
    return `/${store.slug}`;
  }

  protected productLink(product: FrontendAssistantProductCard): string {
    return `/${product.slug || product.id}`;
  }

  /**
   * A coupon has no page of its own; it lives on its store's page — which is also where the code
   * is revealed, on a click that is attributed to the store. The chat never prints the code.
   */
  protected couponLink(coupon: FrontendAssistantCouponCard): string | null {
    return coupon.store?.slug ? `/${coupon.store.slug}` : null;
  }

  /** The percentage the admin's own badge text advertises, or null when it names none. */
  protected badgeDiscount(coupon: FrontendAssistantCouponCard): string {
    const text = (coupon.couponBadges || []).join(' ');
    const match = text.match(/(\d{1,3})\s*%/);
    return match ? `${match[1]}% off` : '';
  }

  /**
   * Product and store artwork is hosted on the merchants' own CDNs, so an image can fail for
   * reasons outside this site. Hiding the broken one leaves the card as a clean title-and-price
   * block rather than a blank frame.
   */
  protected hideBrokenImage(event: Event): void {
    const img = event.target as HTMLImageElement | null;
    if (img) img.style.display = 'none';
  }

  protected trackMessage = (_: number, message: { id: number }) => message.id;
}
